import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar, CheckCircle2, XCircle, RefreshCw, Mail, HardDrive, Loader2,
  ScrollText, Sparkles, HandCoins, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ADMIN_TILES = [
  { to: "/team", label: "Team & Access", desc: "Add / deactivate members, reset passwords, view last login", icon: Users },
  { to: "/settings/incentive-slabs", label: "Engineer Incentives", desc: "Edit ₹ slabs for site visits & installations", icon: HandCoins },
  { to: "/settings/audit-logs", label: "Audit Logs", desc: "12-month log of sensitive actions across the system", icon: ScrollText },
  { to: "/settings/cleanup", label: "Data Cleanup", desc: "Find orphan tasks, procurement & blank projects", icon: Sparkles },
];

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/integrations/calendar/status").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const p = params.get("calendar");
    if (p === "connected") { toast.success("Google Calendar connected"); setParams({}); load(); }
    if (p === "error") { toast.error("Google Calendar connection failed"); setParams({}); }
  }, [params, setParams]);

  const connect = async () => {
    try {
      setBusy(true);
      const { data } = await api.get("/oauth/calendar/start");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start OAuth — check server config");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/integrations/calendar/disconnect"); toast.success("Disconnected"); load(); }
    finally { setBusy(false); }
  };

  const resync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/calendar/resync");
      toast.success(`Synced ${data.synced} installations to Google Calendar`);
    } catch (e) { toast.error(e.response?.data?.detail || "Resync failed"); }
    finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setBusy(true);
    try { await api.post("/notifications/payment-digest/send"); toast.success("Payment digest sent"); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Configuration</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Settings & Integrations</h1>
      </div>

      {/* Admin tiles */}
      <AdminTiles />

      {/* Google Calendar */}
      <Card className="p-6 bg-card border-border">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded bg-primary/10 border border-primary/30 grid place-items-center">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="font-display font-semibold">Google Calendar Sync</h3>
              {status?.connected ? (
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border" data-testid="calendar-status-connected">
                  <CheckCircle2 className="h-3 w-3 mr-1" />Connected
                </Badge>
              ) : status?.configured ? (
                <Badge variant="outline" data-testid="calendar-status-ready">Ready to connect</Badge>
              ) : (
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 border" data-testid="calendar-status-unconfigured">
                  <XCircle className="h-3 w-3 mr-1" />Needs credentials
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              One-way push: whenever an installation is scheduled or updated in TappAV Ops, an event is
              created/updated on <span className="font-mono text-primary">{status?.calendar_id || "your calendar"}</span>.
            </p>
            {!status?.configured && (
              <div className="mt-3 p-3 rounded border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200/90">
                Set <span className="font-mono">GOOGLE_CLIENT_ID</span> and <span className="font-mono">GOOGLE_CLIENT_SECRET</span> in
                <span className="font-mono"> /app/backend/.env</span>, then restart the backend. Get credentials from
                Google Cloud Console → APIs & Services → Credentials → OAuth Client ID (Web application).
                Add redirect URI: <span className="font-mono">{`${window.location.origin}/api/oauth/calendar/callback`}</span>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              {status?.connected ? (
                <>
                  <Button onClick={resync} disabled={busy} data-testid="calendar-resync-btn">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Resync installations
                  </Button>
                  <Button variant="outline" onClick={disconnect} disabled={busy} data-testid="calendar-disconnect-btn">
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button onClick={connect} disabled={busy || !status?.configured} data-testid="calendar-connect-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calendar className="h-4 w-4 mr-2" />}
                  Connect Google Calendar
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Email */}
      <Card className="p-6 bg-card border-border">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded bg-primary/10 border border-primary/30 grid place-items-center">
            <Mail className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="font-display font-semibold">Email Notifications</h3>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">
                <CheckCircle2 className="h-3 w-3 mr-1" />Active
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Sent via managed Resend integration. Automatic triggers: new approval requested,
              approval decided, and a nightly payment-pending digest at 08:00 IST.
            </p>
            <Button className="mt-4" onClick={sendDigest} disabled={busy} data-testid="send-digest-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Send payment digest now
            </Button>
          </div>
        </div>
      </Card>

      {/* Storage */}
      <Card className="p-6 bg-card border-border">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded bg-primary/10 border border-primary/30 grid place-items-center">
            <HardDrive className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="font-display font-semibold">File Attachments Storage</h3>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">
                <CheckCircle2 className="h-3 w-3 mr-1" />Active
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Emergent object storage. Attach documents, images and PDFs to Projects, Service Tickets and Procurement items (max 25MB each).
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

function AdminTiles() {
  const { user } = useAuth();
  if (user?.role !== "admin") return null;
  return (
    <Card className="p-4 bg-card border-border" data-testid="admin-tiles">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Admin Controls</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ADMIN_TILES.map((t) => (
          <Link key={t.to} to={t.to}
                className="group flex items-center gap-3 p-3 rounded border border-border bg-secondary/30 hover:border-primary/60 hover:bg-secondary/50 transition-colors"
                data-testid={`admin-tile-${t.to.split("/").pop()}`}>
            <div className="h-9 w-9 shrink-0 rounded bg-primary/10 border border-primary/30 grid place-items-center">
              <t.icon className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{t.label}</div>
              <div className="text-[11px] text-muted-foreground truncate">{t.desc}</div>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </Link>
        ))}
      </div>
    </Card>
  );
}
